import { getFriendlyErrorMessage } from "utils/errors";

/** What asked for a code: the visitor's Resend, or the screen itself after
 * the provider ended the code on screen. */
export type SendTrigger = "resend" | "code_ended";

/** What a screen's submit tells the code box. `code_ended` means the
 * provider ended the code's session, so the box asks for a new one. */
export type SubmitOutcome = "code_ended" | undefined;

/** What a screen that sends the replacement itself tells its code box:
 * `new_code_sent` means the box is to clear and take focus. */
export type NewCodeOutcome = "new_code_sent" | undefined;

/** Whether the one automatic send for an ended code is still unused. */
export type EndedCodeResend = "available" | "spent";

/** What the screen does when a verify says the code ended. Each ended code
 * earns one automatic send: a send that failed is not repeated by the next
 * submit of the same dead code, the visitor's Resend is the way on. */
export function resendOnEndedCode(state: EndedCodeResend): {
  sendNow: boolean;
  next: EndedCodeResend;
} {
  return { sendNow: state === "available", next: "spent" };
}

/** The state once a new code has gone out, by either trigger: that code
 * can end in turn, and earns its own automatic send. */
export function afterCodeSent(): EndedCodeResend {
  return "available";
}

/** Said under the code box once a new code was texted after the old one
 * ended. */
export const CODE_RESENT_NOTICE =
  "That code didn't work, so we texted you a new one.";

/** Said when the code on screen cannot be used and no automatic send is left
 * for it. */
export const CODE_ENDED_RESEND_PROMPT =
  "That code can't be used anymore. Tap Resend for a new one.";

/** Said when the provider refused the automatic send. The destination is
 * never in it: the screen already shows it. */
export const AUTO_SEND_REFUSED_MESSAGE =
  "We can't send another code right now. Please wait a few minutes and tap Resend.";

/** The client SDK's answer for a code whose session ended. */
const PHONE_UPDATE_CODE_ENDED = "auth/code-expired";

const PHONE_UPDATE_REFUSALS: readonly string[] = [
  "auth/too-many-attempts-try-later",
  "auth/too-many-requests",
];

export function phoneUpdateCodeEnded(errorCode: string | undefined): boolean {
  return errorCode === PHONE_UPDATE_CODE_ENDED;
}

/** The copy for a failed request for a code in the account phone change. */
export function phoneUpdateSendFailureMessage(
  errorCode: string,
  trigger: SendTrigger,
): string {
  if (trigger === "code_ended" && PHONE_UPDATE_REFUSALS.includes(errorCode)) {
    return AUTO_SEND_REFUSED_MESSAGE;
  }
  return getFriendlyErrorMessage(errorCode);
}
